import { type FixtureCanCombine, withoutLeft, withoutRight } from "./canCombine";
import { restrictCanCombine, type ShelfWidthLike } from "./shelfMerchandising";

export interface SplitBayLike {
  splitAfter?: boolean;
  /** With `pallet`, finds the bay an `under_bay` pallet stands under. */
  uniqueId?: string;
  pallet?: { placement: string; bayUniqueId: string | null };
}

export interface SplitBayWithShelves<TShelf extends ShelfWidthLike> extends SplitBayLike {
  shelves: readonly TShelf[];
}

/** `bayNos` are 1-based. */
export interface AisleSegment {
  index: number;
  bayNos: number[];
}

// Mirrors PlanogramBay.getBayAbove: such a pallet takes no floor of its own along the run.
const bayAboveIndexOf = (bays: readonly SplitBayLike[], bay: SplitBayLike) => {
  const bayUniqueId = bay.pallet?.placement === "under_bay" ? bay.pallet.bayUniqueId : null;
  if (bayUniqueId === null) {
    return -1;
  }
  return bays.findIndex((other) => other.uniqueId === bayUniqueId && !other.pallet);
};

/** The bays that stand along the run, in walk order, as indices into `bays`. */
const runIndicesOf = (bays: readonly SplitBayLike[]) =>
  bays.flatMap((bay, bayIndex) => (bayAboveIndexOf(bays, bay) === -1 ? [bayIndex] : []));

/** Aligned with `bays`: whether a declared split cuts each bay off from its neighbour on that side.
 *  A split after the last bay of the run is no seam: the run ends there anyway. */
export const splitSidesOf = (bays: readonly SplitBayLike[]) => {
  const runIndices = runIndicesOf(bays);
  const splitSides = bays.map(() => ({ left: false, right: false }));

  runIndices.forEach((bayIndex, runPosition) => {
    const nextBayIndex = runIndices[runPosition + 1];
    if (nextBayIndex !== undefined && bays[bayIndex].splitAfter === true) {
      splitSides[bayIndex].right = true;
      splitSides[nextBayIndex].left = true;
    }
  });

  return splitSides;
};

export const hasSeamAfter = (bays: readonly SplitBayLike[], bayIndex: number) =>
  splitSidesOf(bays)[bayIndex]?.right ?? false;

/** The pogger's `Bay.split_group`, aligned with `bays`: every bay is 1 on an unsplit run. A
 *  pallet under a gondola bay is in that bay's group. */
export const splitGroupsOf = (bays: readonly SplitBayLike[]): number[] => {
  const splitSides = splitSidesOf(bays);
  const groupOfRunBay = new Map<number, number>();
  let group = 1;
  for (const bayIndex of runIndicesOf(bays)) {
    if (splitSides[bayIndex].left) {
      group += 1;
    }
    groupOfRunBay.set(bayIndex, group);
  }

  return bays.map(
    (bay, bayIndex) =>
      groupOfRunBay.get(bayIndex) ?? groupOfRunBay.get(bayAboveIndexOf(bays, bay)) ?? 1,
  );
};

export const segmentsOf = (bays: readonly SplitBayLike[]): AisleSegment[] => {
  const segments: AisleSegment[] = [];

  splitGroupsOf(bays).forEach((group, bayIndex) => {
    const segment = segments[group - 1] ?? { index: group, bayNos: [] };
    segment.bayNos.push(bayIndex + 1);
    segments[group - 1] = segment;
  });

  return segments;
};

export interface RestrictedShelfCanCombine<TShelf> {
  bayIndex: number;
  shelf: TShelf;
  canCombine: FixtureCanCombine;
}

/** Only ever removes a merge direction, so `canCombineOf` must give the shelf's own value. */
export const restrictCanCombineAtSplits = <TShelf extends ShelfWidthLike>(
  bays: readonly SplitBayWithShelves<TShelf>[],
  canCombineOf: (shelf: TShelf, bayIndex: number) => number | null | undefined,
): RestrictedShelfCanCombine<TShelf>[] => {
  const splitSides = splitSidesOf(bays);

  return bays.flatMap((bay, bayIndex) =>
    bay.shelves.map((shelf) => {
      let canCombine = restrictCanCombine(canCombineOf(shelf, bayIndex), shelf);
      if (splitSides[bayIndex].right) {
        canCombine = withoutRight(canCombine);
      }
      if (splitSides[bayIndex].left) {
        canCombine = withoutLeft(canCombine);
      }

      return { bayIndex, shelf, canCombine };
    }),
  );
};
